import React, { useEffect, useState, useCallback } from "react";
import chroma from "chroma-js";
import Toolbar from "./Toolbar";
import { palette_container } from "../../styles/modules/utilities.module.css";
import Color from "./Color";

const colorModes = ["lch", "lab", "hsl", "hsv", "rgb"];

const generateRandomColor = () => {
  const mode = colorModes[Math.floor(Math.random() * colorModes.length)];
  return chroma.random().alpha(1).hex(mode);
};

const generateRandomPalette = (lockedColors = {}) => {
  return Array(5)
    .fill(null)
    .map((_, i) => (lockedColors[i] ? lockedColors[i] : generateRandomColor()));
};

// Generar paleta armónica basada en un color
const generateHarmonicPalette = (baseColor, lockedColors = {}) => {
  const base = chroma(baseColor);
  const colors = [
    base.darken(1.5).hex(),
    base.darken(0.5).hex(),
    base.hex(),
    base.brighten(0.5).hex(),
    base.brighten(1.5).hex(),
  ];
  return colors.map((c, i) => (lockedColors[i] ? lockedColors[i] : c));
};

// Generar paleta análoga
const generateAnalogousPalette = (baseColor, lockedColors = {}) => {
  const base = chroma(baseColor);
  const hue = base.get("hsl.h");
  const colors = [-30, -15, 0, 15, 30].map(offset =>
    chroma
      .hsl((hue + offset + 360) % 360, base.get("hsl.s"), base.get("hsl.l"))
      .hex()
  );
  return colors.map((c, i) => (lockedColors[i] ? lockedColors[i] : c));
};

// Generar paleta complementaria
const generateComplementaryPalette = (baseColor, lockedColors = {}) => {
  const base = chroma(baseColor);
  const hue = base.get("hsl.h");
  const complementHue = (hue + 180) % 360;
  const colors = [
    base.darken(0.5).hex(),
    base.hex(),
    chroma.hsl(complementHue, base.get("hsl.s"), base.get("hsl.l")).hex(),
    chroma
      .hsl(complementHue, base.get("hsl.s"), base.get("hsl.l"))
      .brighten(0.5)
      .hex(),
    chroma
      .hsl(complementHue, base.get("hsl.s"), base.get("hsl.l"))
      .darken(0.5)
      .hex(),
  ];
  return colors.map((c, i) => (lockedColors[i] ? lockedColors[i] : c));
};

export const randomPalette = (lockedColors = {}) => {
  return generateRandomPalette(lockedColors);
};

function GeneratePalette({ path }) {
  const getInitialPalette = () => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("generatePalette");
      return saved
        ? JSON.parse(saved)
        : [`#7052ff`, `#d06aff`, `#ff81ff`, `#ff7360`, `#ffff51`];
    }
    return [`#7052ff`, `#d06aff`, `#ff81ff`, `#ff7360`, `#ffff51`];
  };

  const [colors, setColors] = useState(getInitialPalette);
  const [lockedColors, setLockedColors] = useState({});
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Guardar en localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("generatePalette", JSON.stringify(colors));
    }
  }, [colors]);

  // Agregar al historial
  const addToHistory = useCallback(
    newColors => {
      setHistory(prev => {
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push(newColors);
        return newHistory.slice(-20); // Mantener últimos 20
      });
      setHistoryIndex(prev => Math.min(prev + 1, 19));
    },
    [historyIndex]
  );

  // Nueva paleta aleatoria
  const newPalette = useCallback(() => {
    const newColors = generateRandomPalette(lockedColors);
    setColors(newColors);
    addToHistory(newColors);
  }, [lockedColors, addToHistory]);

  // Paleta armónica
  const newHarmonicPalette = useCallback(() => {
    const baseColor = colors.find((_, i) => !lockedColors[i]) || colors[2];
    const newColors = generateHarmonicPalette(baseColor, lockedColors);
    setColors(newColors);
    addToHistory(newColors);
  }, [colors, lockedColors, addToHistory]);

  // Paleta análoga
  const newAnalogousPalette = useCallback(() => {
    const baseColor = colors.find((_, i) => !lockedColors[i]) || colors[2];
    const newColors = generateAnalogousPalette(baseColor, lockedColors);
    setColors(newColors);
    addToHistory(newColors);
  }, [colors, lockedColors, addToHistory]);

  // Paleta complementaria
  const newComplementaryPalette = useCallback(() => {
    const baseColor = colors.find((_, i) => !lockedColors[i]) || colors[2];
    const newColors = generateComplementaryPalette(baseColor, lockedColors);
    setColors(newColors);
    addToHistory(newColors);
  }, [colors, lockedColors, addToHistory]);

  // Bloquear/desbloquear color
  const toggleLock = useCallback(
    index => {
      setLockedColors(prev => {
        const newLocked = { ...prev };
        if (newLocked[index]) {
          delete newLocked[index];
        } else {
          newLocked[index] = colors[index];
        }
        return newLocked;
      });
    },
    [colors]
  );

  // Cambiar color individual
  const changeColor = useCallback(
    (index, newColor) => {
      const newColors = [...colors];
      newColors[index] = newColor;
      setColors(newColors);
      addToHistory(newColors);
    },
    [colors, addToHistory]
  );

  // Deshacer
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setColors(history[historyIndex - 1]);
    }
  }, [history, historyIndex]);

  // Rehacer
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setColors(history[historyIndex + 1]);
    }
  }, [history, historyIndex]);

  // Atajos de teclado
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.target.tagName === "INPUT") return;

      if (e.code === "Space") {
        e.preventDefault();
        newPalette();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      } else if (e.key >= "1" && e.key <= "5") {
        toggleLock(parseInt(e.key) - 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [newPalette, undo, redo, toggleLock]);

  return (
    <>
      <Toolbar
        colorPalette={colors}
        newPalette={newPalette}
        newHarmonicPalette={newHarmonicPalette}
        newAnalogousPalette={newAnalogousPalette}
        newComplementaryPalette={newComplementaryPalette}
        undo={undo}
        redo={redo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
      />

      <div className={palette_container}>
        {colors.map((color, i) => (
          <Color
            path={path}
            key={i}
            index={i}
            color={color}
            withLinkColor={true}
            isLocked={!!lockedColors[i]}
            onToggleLock={() => toggleLock(i)}
            onChangeColor={newColor => changeColor(i, newColor)}
          />
        ))}
      </div>
    </>
  );
}

export default GeneratePalette;
